import * as THREE from 'three';
import { roomWalker } from '../state/roomBody';
import React, { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { ROOMS } from '../rooms';
import { useWorldStore } from '../state/worldStore';
import { roomSpace } from '../state/interiorFloor';
import { cafeArtists } from '../state/cafeArtists';

const roomP = new THREE.Vector3();

// CafeCounter — the café's floor: where you can sit, and your coffee.
//
// Khari, 2026-10-30 (The Jahva House): "a barista and tables you can sit at …
// take your order and bring it to a table (if any are open), and if none are
// open, you can get it from the bar. it can just disappear to say it was drank."
//
//   SEATS   a chair offers "Sit down" when you are close to it (RoomMarkers draws
//           the halo and the button; this only says WHICH chairs, the few
//           nearest you on your floor). Every chair at once would be a wall of
//           buttons; none would mean the salon's rule that a scene must point
//           at a seat first, and a café has no scene.
//   WHERE   you are, for the barista (`cafeSeat`: your table, or how many are
//           free), sent with every line at the counter (ShopDock).
//   ORDER   placed only by the barista's take_order (ShopDock). Made, then
//           walked to your table by whoever is on shift (`actor:<job>` staged to
//           the table's serve mark, InteriorResidents); standing with a table
//           free, it waits a while for you to sit; otherwise it is left at the
//           pickup end of the bar. On the table or the bar for a few minutes,
//           then gone: drunk.
//
// The room supplies the places (rooms.js: `tables` [{ id, seats, cups, serve }],
// `pickup`), in the room group's own space, the same space as the residents and
// the seat buttons: so the cup is sized against a person (a resident is ~0.75
// units tall), not against the building's fit.
//
// SHARED SEATS (2026-10-30). Khari: "This indoor room is a shared live
// experience (like the plaza), so seats will actually be limited. Prevent
// sitting at one if it is taken, and limit the time a user can sit (2 min)."
// In a `sharedSeats` room the server says who holds which chair (worldStore
// `roomSeats`): a chair somebody else holds is never offered, and "open" is a
// table nobody else is sitting at — so "every table is taken, it waits at the
// bar" is something the room can actually say. Your own table never counts as
// open (you are at it). Without the server's map (offline, or a room without
// the flag) only you are known, as before.
const SIT_NEAR = 1.4;        // world units: a chair this close offers itself
const SIT_LEVEL = 0.5;       // and on your floor (the loft is up a flight)
const SIT_MAX = 4;
const MAKE_MS = 9000;        // pulling the shot, steaming the milk
const WALK = 0.5;            // world units a second: InteriorResidents' own walking pace
const HAND_MS = 2500;        // set it down, a word, back behind the bar
const LOFT_Y = 1.6;          // eye height above which you are upstairs (the spoken-word corner)
const WAIT_SEAT_MS = 90000;  // standing with a table free: it waits this long for you to sit
const DRINK_MS = 4 * 60000;  // then it is gone: drunk

function tableOf(tables, target) {
  for (const t of tables) {
    const i = (t.seats || []).indexOf(target);
    if (i >= 0) return { t, i };
  }
  return null;
}

/**
 * THE WAY ROUND THE COUNTER (rooms.js: `stage['*'].gate` / `gate_out`, from the
 * room's own builder): residents walk to a mark in a straight line, and a
 * straight line from behind the bar to a table goes through the counter. So the
 * barista is sent mark by mark, each leg timed from its length at walking pace.
 * [{ mark, ms }] — out to the table, or back (mark null = their own spot).
 */
function route(any, to, back = false) {
  const marks = back ? ['gate_out', 'gate', null] : ['gate', 'gate_out', to];
  const legs = [];
  let from = any.bar?.pos || null;
  if (back) from = any[to]?.pos || null;
  for (const m of marks) {
    if (m && !any[m]) continue;   // a room without the way round walks straight
    const at = m ? any[m].pos : any.bar?.pos;
    const d = from && at ? Math.hypot(at[0] - from[0], at[2] - from[2]) : 2;
    legs.push({ mark: m, ms: (d / WALK) * 1000 + 300 });
    from = at;
  }
  return legs;
}

/** Which leg of a route is under way `ms` in: { i, done }. */
function legAt(legs, ms) {
  let t = 0;
  for (let i = 0; i < legs.length; i++) { t += legs[i].ms; if (ms < t) return { i, done: false }; }
  return { i: legs.length - 1, done: true };
}

function Cup({ at }) {
  // A cup on its saucer, ~9 cm tall at a person's scale. Three cheap parts.
  return (
    <group position={at}>
      <mesh position={[0, 0.003, 0]}>
        <cylinderGeometry args={[0.03, 0.028, 0.005, 20]} />
        <meshStandardMaterial color="#f2ede4" roughness={0.45} metalness={0} />
      </mesh>
      <mesh position={[0, 0.022, 0]}>
        <cylinderGeometry args={[0.019, 0.015, 0.034, 18]} />
        <meshStandardMaterial color="#f6f2ea" roughness={0.4} metalness={0} />
      </mesh>
      <mesh position={[0, 0.0395, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.0175, 18]} />
        <meshStandardMaterial color="#6b4428" roughness={0.3} metalness={0} />
      </mesh>
    </group>
  );
}

export default function CafeCounter({ roomId }) {
  const room = ROOMS[roomId];
  const tables = room?.tables?.length ? room.tables : null;
  const camera = useThree((s) => s.camera);
  const order = useWorldStore((s) => s.cafeOrder);
  const acc = useRef(0);
  const marked = useRef('');
  const handTimer = useRef(null);

  // The tables' serve spots as the job's marks ('*': whoever is on shift), so a
  // barista can be sent to `serve_<table>`. Added once, beside the room's own.
  useEffect(() => {
    if (!tables) return;
    room.stage = room.stage || {};
    const any = { ...(room.stage['*'] || {}) };
    for (const t of tables) if (t.serve?.pos) any[`serve_${t.id}`] = { pos: t.serve.pos, rotY: t.serve.rotY || 0 };
    room.stage['*'] = any;
  }, [room, tables]);

  // Out of the room: the seat prompts, your place at the counter and an unserved
  // order go with you (a cup on a table in a room you left is simply drunk).
  useEffect(() => () => {
    const ws = useWorldStore.getState();
    clearTimeout(handTimer.current);
    if (marked.current) ws.setMarks([]);
    ws.setCafeSeat(null);
    if (ws.cafeOrder?.zone === roomId) ws.placeCafeOrder(null);
  }, [roomId]);

  useFrame((_, dt) => {
    if (!tables) return;
    acc.current += dt;
    if (acc.current < 0.25) return;
    acc.current = 0;
    const ws = useWorldStore.getState();
    const now = Date.now();
    const seated = ws.seated?.phase && ws.seated.phase !== 'standing' ? ws.seated.target : null;
    // Chairs somebody else holds right now (SHARED SEATS, above).
    const holds = room.sharedSeats && ws.roomSeats?.zone === roomId ? ws.roomSeats.seats : null;
    // …and a chair a city artist is sitting in (CafeGuests) is theirs, not offered.
    const taken = (name) => { const h = holds?.[name]; return (!!h && !h.mine && h.until > now) || cafeArtists.bySeat.has(name); };

    // ── SEATS near you ──────────────────────────────────────────────────────
    // In ROOM space: a sited café stands at its building, turned with it
    // (2026-10-31), and the tables' points are the room's, not the world's.
    const p = roomSpace.object ? roomSpace.object.worldToLocal(roomP.copy(roomWalker(camera))) : roomWalker(camera);
    const near = [];
    for (const t of tables) {
      (t.seats || []).forEach((name, i) => {
        const at = t.cups?.[i];
        if (!at || taken(name)) return;
        const d = Math.hypot(at[0] - p.x, at[2] - p.z);
        if (d < SIT_NEAR && Math.abs(at[1] - (p.y - 0.4)) < SIT_LEVEL + 0.4) near.push({ name, d });
      });
    }
    near.sort((a, b) => a.d - b.d);
    const targets = near.slice(0, SIT_MAX).map((n) => n.name);
    // Upstairs, the spoken-word corner's couch and chairs (they belong to no table).
    if (p.y > LOFT_Y) for (const name of room.spokenSeats || []) if (targets.length < SIT_MAX + 3 && !taken(name)) targets.push(name);
    if (seated && !targets.includes(seated)) targets.unshift(seated);   // your own chair keeps its "Stand up"
    const key = targets.join('|');
    if (key !== marked.current) { marked.current = key; ws.setMarks(targets.map((target) => ({ target }))); }

    // ── WHERE YOU ARE, for the barista ──────────────────────────────────────
    const mine = seated ? tableOf(tables, seated) : null;
    const open = tables.filter((t) => t !== mine?.t && !(t.seats || []).some(taken)).length;
    ws.setCafeSeat({ table: mine?.t.id || null, open });

    // ── YOUR ORDER ──────────────────────────────────────────────────────────
    const o = ws.cafeOrder;
    if (!o || o.zone !== roomId) return;
    const actorKey = o.roleKey ? `actor:${o.roleKey}` : null;
    const any = room.stage?.['*'] || {};
    if (o.phase === 'making') {
      if (now - o.at < MAKE_MS) return;
      if (mine) {
        ws.patchCafeOrder({ phase: 'serving', table: mine.t.id, seatIndex: mine.i, servingAt: now, legs: route(any, `serve_${mine.t.id}`), leg: -1 });
      } else if (now - o.at >= MAKE_MS + WAIT_SEAT_MS || !open) {
        // Nobody sat down, or every table is taken (`open` is real now, in a
        // shared room): it waits at the end of the bar.
        ws.patchCafeOrder({ phase: 'served', table: null, cup: room.pickup || null, servedAt: now });
      }
      return;
    }
    if (o.phase === 'serving') {
      const at = legAt(o.legs || [], now - o.servingAt);
      if (!at.done) {
        if (at.i !== o.leg) { if (actorKey) ws.stageTo(actorKey, o.legs[at.i].mark); ws.patchCafeOrder({ leg: at.i }); }
        return;
      }
      const t = tables.find((x) => x.id === o.table);
      ws.patchCafeOrder({ phase: 'served', cup: t?.cups?.[o.seatIndex || 0] || room.pickup || null, servedAt: now });
      // Set down, a word, and back the way they came.
      const back = route(any, `serve_${o.table}`, true);
      clearTimeout(handTimer.current);
      let i = 0;
      const step = () => {
        const leg = back[i];
        if (!leg || !actorKey) return;
        useWorldStore.getState().stageTo(actorKey, leg.mark);
        i += 1;
        if (i < back.length) handTimer.current = setTimeout(step, leg.ms);
      };
      handTimer.current = setTimeout(step, HAND_MS);
      return;
    }
    if (o.phase === 'served' && now - o.servedAt >= DRINK_MS) ws.placeCafeOrder(null);
  });

  if (!tables || !order || order.zone !== roomId || order.phase !== 'served' || !order.cup) return null;
  return <Cup at={order.cup} />;
}
